import React, { useState, useEffect } from 'react';
import { useApi } from '../../../hooks/useApi';
import { useAuth } from '../../../hooks/useAuth';
import logger from "../../../utils/logger";
import { fmtDate, fmtDateTime } from '../../../utils/ruDates';
function AdminUsers() {
  const {
    request,
    showStatus
  } = useApi();
  const {
    user: currentUser
  } = useAuth();
  const [selectedIds, setSelectedIds] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  // Раньше грузили плоский limit=1000 и фильтровали на клиенте — при росте базы
  // сверх 1000 пользователи "терялись" из поиска молча. Теперь поиск/фильтр по
  // роли уходят на сервер, тут просто постраничная навигация.
  const PAGE_SIZE = 100;
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  // Плательщик, кабинеты и проекты пользователя — заменили раздел «Компании».
  // Доступ живёт в членствах, а не в company_id, поэтому собираем его здесь.
  const [access, setAccess] = useState(null);
  const [payerForm, setPayerForm] = useState({ name: '', inn: '', legal_address: '' });
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: '',
    status: '',
    phone: '',
    telegram_id: '',
    verified_identity: false,
    anylex_recommended: false,
    email_verified: false,
    password: '' // New password if provided
  });
  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        skip: String(page * PAGE_SIZE),
      });
      if (searchTerm.trim()) params.set('search', searchTerm.trim());
      if (roleFilter) params.set('role', roleFilter);
      const data = await request(`/api/v1/users/?${params.toString()}`);
      const rows = data || [];
      setUsers(rows);
      setHasMore(rows.length === PAGE_SIZE);
      setSelectedIds([]); // Reset selection on reload
    } catch (error) {
      logger.error('Failed to load users', error);
    } finally {
      setLoading(false);
    }
  };
  const handleBulkAction = async action => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Вы уверены, что хотите применить "${action}" к ${selectedIds.length} пользователям?`)) return;
    try {
      await request('/api/v1/admin/users/bulk-action', {
        method: 'POST',
        body: JSON.stringify({
          user_ids: selectedIds,
          action
        })
      });
      showStatus('success', `Действие "${action}" выполнено`);
      loadUsers();
    } catch (error) {
      showStatus('error', 'Ошибка выполнения массового действия');
    }
  };
  const handleSelectAll = e => {
    if (e.target.checked) {
      setSelectedIds(filteredUsers.map(u => u.id));
    } else {
      setSelectedIds([]);
    }
  };
  const handleSelectOne = id => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };
  const [editTab, setEditTab] = useState('profile'); // profile, logs, permissions
  const [userLogs, setUserLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [userPermissions, setUserPermissions] = useState([]);
  const [authProviders, setAuthProviders] = useState([]);
  useEffect(() => {
    loadAvailablePermissions();
    // eslint-disable-next-line
  }, []);

  // Сброс на первую страницу при смене поиска/фильтра роли.
  useEffect(() => {
    setPage(0);
  }, [searchTerm, roleFilter]);

  // Debounce, чтобы не долбить сервер на каждое нажатие клавиши в поиске.
  useEffect(() => {
    const t = setTimeout(loadUsers, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [page, searchTerm, roleFilter]);
  const loadAvailablePermissions = async () => {
    try {
      const perms = await request('/api/v1/admin/permissions');
      setAvailablePermissions(perms || {});
    } catch (e) {
      logger.error('Failed to load available perms', e);
    }
  };
  const loadUserLogs = async userId => {
    setLogsLoading(true);
    try {
      const data = await request(`/api/v1/admin/logs?user_id=${userId}&limit=20`);
      setUserLogs(data.items || []);
    } catch (error) {
      logger.error('Failed to load user logs', error);
    } finally {
      setLogsLoading(false);
    }
  };
  const handleEdit = async user => {
    setEditingUser(user);
    setEditTab('profile');
    setUserLogs([]);

    // Load full detail to get permissions
    try {
      const detail = await request(`/api/v1/admin/users/${user.id}/detail`);
      setUserPermissions(detail.admin_permissions || []);
      setAuthProviders(detail.auth_providers || []);
      setAccess({
        payer: detail.payer || null,
        cabinets: detail.cabinets || [],
        projects: detail.projects || []
      });
      setPayerForm({
        name: detail.payer?.name || '',
        inn: detail.payer?.inn || '',
        legal_address: detail.payer?.legal_address || ''
      });
    } catch (e) {
      setUserPermissions([]);
      setAuthProviders([]);
      setAccess(null);
    }
    setEditForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      role: user.role || 'viewer',
      status: user.status || 'active',
      phone: user.phone || '',
      telegram_id: user.telegram_id || '',
      verified_identity: !!user.verified_identity,
      anylex_recommended: !!user.anylex_recommended,
      email_verified: !!user.email_verified,
      password: ''
    });
  };
  const reloadAccess = async () => {
    if (!editingUser) return;
    try {
      const detail = await request(`/api/v1/admin/users/${editingUser.id}/detail`);
      setAccess({
        payer: detail.payer || null,
        cabinets: detail.cabinets || [],
        projects: detail.projects || []
      });
    } catch (e) {
      logger.error('Не удалось перезагрузить доступы', e);
    }
  };
  const handleSavePayer = async () => {
    if (!access?.payer) return;
    try {
      await request(`/api/v1/admin/payers/${access.payer.company_id}`, {
        method: 'PATCH',
        body: JSON.stringify(payerForm)
      });
      showStatus('success', 'Реквизиты сохранены');
      reloadAccess();
    } catch (e) {
      showStatus('error', 'Ошибка сохранения реквизитов');
    }
  };
  const CABINET_ROLES = [['viewer', 'Наблюдатель'], ['finance', 'Финансы'], ['manager', 'Менеджер'], ['admin_api', 'Админ API'], ['admin', 'Администратор']];
  const PROJECT_ROLES = CABINET_ROLES.filter(([value]) => value !== 'viewer');
  const roleSelectStyle = { padding: '2px 4px', marginRight: '10px' };
  const handleCabinetRole = async (membershipId, role, currentRole) => {
    if (!role || role === currentRole) return;
    try {
      await request(`/api/v1/admin/marketplace-memberships/${membershipId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role })
      });
      showStatus('success', 'Роль на кабинете изменена');
      reloadAccess();
    } catch (e) {
      showStatus('error', 'Не удалось изменить роль. Выберите роль из списка.');
    }
  };
  const handleCabinetRevoke = async (membershipId, accountName) => {
    if (!window.confirm(`Забрать доступ к кабинету «${accountName}»?`)) return;
    try {
      await request(`/api/v1/admin/marketplace-memberships/${membershipId}`, { method: 'DELETE' });
      showStatus('success', 'Доступ забран');
      reloadAccess();
    } catch (e) {
      showStatus('error', e?.message || 'Ошибка снятия доступа');
    }
  };
  const handleProjectRole = async (memberId, role, currentRole) => {
    if (!role || role === currentRole) return;
    try {
      await request(`/api/v1/admin/workspace-members/${memberId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role })
      });
      showStatus('success', 'Роль в проекте изменена');
      reloadAccess();
    } catch (e) {
      showStatus('error', 'Не удалось изменить роль. Выберите роль из списка.');
    }
  };
  const handleProjectRemove = async (memberId, projectName) => {
    if (!window.confirm(`Убрать пользователя из проекта «${projectName}»? Доступ к кабинетам проекта тоже пропадёт.`)) return;
    try {
      await request(`/api/v1/admin/workspace-members/${memberId}`, { method: 'DELETE' });
      showStatus('success', 'Убран из проекта');
      reloadAccess();
    } catch (e) {
      showStatus('error', e?.message || 'Ошибка удаления из проекта');
    }
  };
  const handleSavePermissions = async () => {
    try {
      await request(`/api/v1/admin/users/${editingUser.id}/permissions`, {
        method: 'POST',
        body: JSON.stringify({
          permissions: userPermissions
        })
      });
      showStatus('success', 'Права доступа обновлены');
    } catch (e) {
      showStatus('error', 'Ошибка обновления прав');
    }
  };
  const handleAdminVerifyEmail = async () => {
    if (!editingUser) return;
    try {
      await request(`/api/v1/admin/users/${editingUser.id}/verify-email`, {
        method: 'POST'
      });
      setEditForm(prev => ({
        ...prev,
        email_verified: true
      }));
      setUsers(prev => prev.map(item => item.id === editingUser.id ? {
        ...item,
        email_verified: true
      } : item));
      showStatus('success', 'Email подтвержден.');
    } catch (error) {
      showStatus('error', 'Не удалось подтвердить email.');
    }
  };
  const handleUnlinkProvider = async providerName => {
    if (!editingUser || !providerName || providerName === 'email') return;
    try {
      await request(`/api/v1/admin/users/${editingUser.id}/auth-providers/${providerName}`, {
        method: 'DELETE'
      });
      setAuthProviders(prev => prev.filter(item => item.provider !== providerName));
      showStatus('success', `Провайдер ${providerName} отвязан.`);
    } catch (error) {
      showStatus('error', 'Не удалось отвязать провайдер.');
    }
  };
  const handleSaveEdit = async e => {
    e.preventDefault();
    try {
      const payload = {
        ...editForm
      };
      if (!payload.password) delete payload.password; // Don't send empty password

      await request(`/api/v1/users/${editingUser.id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });
      showStatus('success', 'Данные пользователя сохранены');
      setEditingUser(null);
      loadUsers();
    } catch (error) {
      showStatus('error', 'Ошибка сохранения');
    }
  };
  const handleDelete = async id => {
    if (!window.confirm('ВНИМАНИЕ: Это полностью удалит аккаунт и все данные. Продолжить?')) return;
    try {
      await request(`/api/v1/admin/users/${id}`, {
        method: 'DELETE'
      });
      showStatus('success', 'Пользователь удален');
      loadUsers();
    } catch (error) {
      showStatus('error', 'Ошибка удаления');
    }
  };
  const handleStatusToggle = async user => {
    const newStatus = (user.is_active === false || user.status === 'banned') ? 'active' : 'banned';
    const endpoint = (user.is_active === false || user.status === 'banned') ? 'unban' : 'ban';
    try {
      await request(`/api/v1/admin/users/${user.id}/${endpoint}`, {
        method: 'POST',
        body: endpoint === 'ban' ? JSON.stringify({
          reason: 'Admin action'
        }) : null
      });
      showStatus('success', (user.is_active === false || user.status === 'banned') ? 'Разблокирован' : 'Заблокирован');
      loadUsers();
    } catch (error) {
      showStatus('error', 'Ошибка изменения статуса');
    }
  };
  const handleExportCSV = () => {
    const headers = ['ID', 'Email', 'Имя', 'Роль', 'Статус', 'Телефон', 'Дата регистрации'];
    const csvRows = [headers.join(',')];
    users.forEach(u => {
      const row = [u.id, u.email, `"${u.first_name || ''} ${u.last_name || ''}"`, u.role, u.status, `"${u.phone || ''}"`, u.created_at || ''];
      csvRows.push(row.join(','));
    });
    const blob = new Blob([csvRows.join('\n')], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `users_export_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const getRoleBadge = role => {
    const styles = {
      admin: {
        background: '#e74c3c',
        color: 'white'
      },
      manager: {
        background: '#f39c12',
        color: 'white'
      },
      viewer: {
        background: '#3498db',
        color: 'white'
      }
    };
    const style = styles[role] || {
      background: '#95a5a6',
      color: 'white'
    };
    return <span style={{
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold',
      ...style
    }}>{role.toUpperCase()}</span>;
  };
  // Поиск и фильтр по роли теперь применяются на сервере (см. loadUsers) —
  // users уже отфильтрован и постранично нарезан, дополнительная фильтрация
  // на клиенте больше не нужна (и была бы неверной для страниц > 1).
  const filteredUsers = users;
  return <div className="admin-page">
            <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '30px'
    }}>
                <div>
                    <h1 style={{
          margin: 0
        }}>Пользователи</h1>
                    <p style={{
          margin: '5px 0 0',
          color: '#666',
          fontSize: '14px'
        }}>Управление доступом и ролями на уровне всей системы</p>
                </div>
                <div style={{
        display: 'flex',
        gap: '15px'
      }}>
                    <div style={{
          position: 'relative',
          width: '300px'
        }}>
                        <input type="text" placeholder="Поиск..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{
            width: '100%',
            padding: '10px 15px',
            borderRadius: '8px',
            border: '1px solid #ddd',
            fontSize: '14px'
          }} />
            
                        {searchTerm && <button onClick={() => setSearchTerm('')} style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            color: '#999',
            cursor: 'pointer',
            fontSize: '18px'
          }}>
              
                                &times;
                            </button>}
                    </div>
                    <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={{
          padding: '10px',
          borderRadius: '8px',
          border: '1px solid #ddd',
          background: 'white',
          minWidth: '150px'
        }}>
            
                        <option value="">Все роли</option>
                        <option value="admin">Администраторы</option>
                        <option value="user">Пользователи</option>
                        <option value="manager">Менеджеры</option>
                    </select>
                    <button onClick={handleExportCSV} style={{
          padding: '10px 15px',
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          fontWeight: '500'
        }}>
            
                        📊 Экспорт
                    </button>
                </div>
            </div>

            {/* Edit Modal */}
            {editingUser && <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
                    <div style={{
        background: 'white',
        borderRadius: '12px',
        width: '700px',
        maxWidth: '95%',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
                        <div style={{
          padding: '20px 30px',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
                            <h2 style={{
            margin: 0
          }}>Пользователь: {editingUser.email}</h2>
                            <button onClick={() => setEditingUser(null)} style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#999'
          }}>&times;</button>
                        </div>

                        <div style={{
          display: 'flex',
          borderBottom: '1px solid #eee',
          background: '#fcfcfc'
        }}>
                            <button onClick={() => setEditTab('profile')} style={{
            padding: '12px 25px',
            border: 'none',
            background: 'none',
            borderBottom: editTab === 'profile' ? '2px solid #3498db' : 'none',
            color: editTab === 'profile' ? '#3498db' : '#666',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}>
              
                                Профиль
                            </button>
                            <button onClick={() => {
            setEditTab('logs');
            if (userLogs.length === 0) loadUserLogs(editingUser.id);
          }} style={{
            padding: '12px 25px',
            border: 'none',
            background: 'none',
            borderBottom: editTab === 'logs' ? '2px solid #3498db' : 'none',
            color: editTab === 'logs' ? '#3498db' : '#666',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}>
              
                                Аудит (Логи)
                            </button>
                            <button onClick={() => setEditTab('access')} style={{
            padding: '12px 25px',
            border: 'none',
            background: 'none',
            borderBottom: editTab === 'access' ? '2px solid #3498db' : 'none',
            color: editTab === 'access' ? '#3498db' : '#666',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}>
                                Плательщик и доступы
                            </button>
                            {editingUser.role === 'admin' && currentUser?.admin_permissions?.includes('all') && <button onClick={() => setEditTab('permissions')} style={{
            padding: '12px 25px',
            border: 'none',
            background: 'none',
            borderBottom: editTab === 'permissions' ? '2px solid #3498db' : 'none',
            color: editTab === 'permissions' ? '#3498db' : '#666',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}>
              
                                    Права Доступа
                                </button>}
                        </div>

                        <div style={{
          padding: '30px',
          overflowY: 'auto',
          flex: 1
        }}>
                            {editTab === 'profile' && <form onSubmit={handleSaveEdit}>
                                    <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '15px',
              marginBottom: '15px'
            }}>
                                        <div>
                                            <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>Имя</label>
                                            <input type="text" value={editForm.first_name} onChange={e => setEditForm({
                  ...editForm,
                  first_name: e.target.value
                })} style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }} />
                                        </div>
                                        <div>
                                            <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>Фамилия</label>
                                            <input type="text" value={editForm.last_name} onChange={e => setEditForm({
                  ...editForm,
                  last_name: e.target.value
                })} style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }} />
                                        </div>
                                    </div>
                                    <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '15px',
              marginBottom: '15px'
            }}>
                                        <div>
                                            <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>Телефон</label>
                                            <input type="text" value={editForm.phone} onChange={e => setEditForm({
                  ...editForm,
                  phone: e.target.value
                })} style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }} />
                                        </div>
                                        <div>
                                            <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>Telegram ID</label>
                                            <input type="text" autoComplete="off" name="telegram_id_field" value={editForm.telegram_id} onChange={e => setEditForm({
                  ...editForm,
                  telegram_id: e.target.value
                })} style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }} />
                                        </div>
                                    </div>
                                    <div style={{
              marginBottom: '15px'
            }}>
                                        <label style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>Глобальная роль</label>
                                        <select value={editForm.role} onChange={e => setEditForm({
                ...editForm,
                role: e.target.value
              })} style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}>
                                            <option value="viewer">Наблюдатель</option>
                                            <option value="manager">Менеджер</option>
                                            <option value="user">Пользователь</option>
                                            <option value="admin">Администратор (полный доступ)</option>
                                        </select>
                                    </div>
                                    <div style={{
              marginBottom: '15px'
            }}>
                                        <label style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>Новый пароль</label>
                                        <input type="password" autoComplete="new-password" name="admin_edit_new_password" placeholder="Оставьте пустым чтобы не менять" value={editForm.password} onChange={e => setEditForm({
                ...editForm,
                password: e.target.value
              })} style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }} />
                                    </div>

                                    <div style={{
              marginBottom: '15px',
              padding: '12px',
              border: '1px solid #eee',
              borderRadius: '8px',
              background: '#fafafa'
            }}>
                                        <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '10px'
              }}>
                                            <div>
                                                <div style={{
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>Подтверждение email</div>
                                                <div style={{
                    fontSize: '12px',
                    color: editForm.email_verified ? '#27ae60' : '#c05621'
                  }}>
                                                    {editForm.email_verified ? 'Подтвержден' : 'Не подтвержден'}
                                                </div>
                                            </div>
                                            {!editForm.email_verified && <button type="button" onClick={handleAdminVerifyEmail} style={{
                  padding: '8px 12px',
                  background: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}>
                                                    Подтвердить email
                                                </button>}
                                        </div>
                                        <div style={{
                fontSize: '12px',
                fontWeight: 'bold',
                marginBottom: '8px'
              }}>Способы входа</div>
                                        <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                                            {authProviders.length === 0 && <div style={{
                  fontSize: '12px',
                  color: '#666'
                }}>Связанных провайдеров нет</div>}
                                            {authProviders.map(provider => <div key={`${provider.provider}-${provider.provider_user_id}`} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px',
                  borderRadius: '6px',
                  background: 'white',
                  border: '1px solid #eee'
                }}>
                                                    <div>
                                                        <div style={{
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>{provider.provider}</div>
                                                        <div style={{
                      fontSize: '12px',
                      color: '#666'
                    }}>{provider.provider_email || provider.provider_user_id}</div>
                                                    </div>
                                                    {provider.provider !== 'email' && <button type="button" onClick={() => handleUnlinkProvider(provider.provider)} style={{
                    padding: '6px 10px',
                    background: 'transparent',
                    color: '#e53e3e',
                    border: '1px solid #f5c2c7',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}>
                                                            Отвязать
                                                        </button>}
                                                </div>)}
                                        </div>
                                    </div>

                                    <div style={{
              display: 'flex',
              gap: '20px',
              marginBottom: '20px'
            }}>
                                        <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer'
              }}>
                                            <input type="checkbox" checked={editForm.verified_identity} onChange={e => setEditForm({
                  ...editForm,
                  verified_identity: e.target.checked
                })} />
                                            Личность подтверждена
                                        </label>
                                        <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer'
              }}>
                                            <input type="checkbox" checked={editForm.anylex_recommended} onChange={e => setEditForm({
                  ...editForm,
                  anylex_recommended: e.target.checked
                })} />
                                            Рекомендован Энилекс
                                        </label>
                                    </div>

                                    <div style={{
              display: 'flex',
              gap: '10px'
            }}>
                                        <button type="submit" style={{
                flex: 1,
                padding: '12px',
                background: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}>Сохранить</button>
                                        <button type="button" onClick={() => setEditingUser(null)} style={{
                flex: 1,
                padding: '12px',
                background: '#eee',
                color: '#333',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}>Отмена</button>
                                    </div>
                                </form>}
                            {editTab === 'logs' && <div>
                                    {logsLoading && <div style={{
              textAlign: 'center',
              padding: '20px'
            }}>Загрузка истории...</div>}
                                    {!logsLoading && userLogs.length === 0 && <div style={{
              textAlign: 'center',
              padding: '20px',
              color: '#999'
            }}>История действий пуста</div>}
                                    <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}>
                                        {userLogs.map(log => <div key={log.id} style={{
                padding: '10px',
                background: '#f8f9fa',
                borderRadius: '6px',
                fontSize: '13px',
                borderLeft: '3px solid #3498db'
              }}>
                                                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '4px'
                }}>
                                                    <span style={{
                    fontWeight: 'bold'
                  }}>{log.action}</span>
                                                    <span style={{
                    fontSize: '12px',
                    color: '#999'
                  }}>{fmtDateTime(log.created_at)}</span>
                                                </div>
                                                <div style={{
                  color: '#666'
                }}>Объект: {log.entity_name || '—'}</div>
                                            </div>)}
                                    </div>
                                </div>}
                            {editTab === 'access' && <div>
                                {!access && <div style={{ color: '#999' }}>Загрузка…</div>}

                                {access && <>
                                    <h3 style={{ marginTop: 0, fontSize: '16px' }}>Плательщик</h3>
                                    {!access.payer && <div style={{
                                        padding: '15px',
                                        background: '#fff8e1',
                                        borderRadius: '8px',
                                        color: '#8a6d3b',
                                        marginBottom: '25px'
                                    }}>
                                        Реквизиты не привязаны. Баланс и списания для этого пользователя не ведутся.
                                    </div>}
                                    {access.payer && <div style={{
                                        padding: '15px',
                                        background: '#f8fafb',
                                        borderRadius: '8px',
                                        marginBottom: '25px'
                                    }}>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(4, 1fr)',
                                            gap: '15px',
                                            marginBottom: '15px'
                                        }}>
                                            <div>
                                                <div style={{ fontSize: '12px', color: '#999' }}>Баланс</div>
                                                <div style={{ fontWeight: 'bold', fontSize: '18px' }}>{access.payer.balance.toFixed(2)} ₽</div>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '12px', color: '#999' }}>Списание в сутки</div>
                                                <div style={{ fontWeight: 'bold', fontSize: '18px' }}>{access.payer.daily_charge.toFixed(2)} ₽</div>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '12px', color: '#999' }}>Хватит на</div>
                                                <div style={{ fontWeight: 'bold', fontSize: '18px' }}>
                                                    {access.payer.days_left !== null ? access.payer.days_left + ' дн.' : '—'}
                                                </div>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '12px', color: '#999' }}>Кабинетов на балансе</div>
                                                <div style={{ fontWeight: 'bold', fontSize: '18px' }}>{access.payer.cabinets_count}</div>
                                            </div>
                                        </div>
                                        {access.payer.depleted_since && <div style={{
                                            padding: '8px 12px',
                                            background: '#fdecea',
                                            color: '#c0392b',
                                            borderRadius: '6px',
                                            fontSize: '13px',
                                            marginBottom: '15px'
                                        }}>Баланс исчерпан с {access.payer.depleted_since}</div>}

                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: '2fr 1fr',
                                            gap: '12px',
                                            marginBottom: '12px'
                                        }}>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold' }}>Название реквизитов</label>
                                                <input type="text" value={payerForm.name} onChange={e => setPayerForm({ ...payerForm, name: e.target.value })}
                                                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold' }}>ИНН</label>
                                                <input type="text" value={payerForm.inn} onChange={e => setPayerForm({ ...payerForm, inn: e.target.value })}
                                                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                            </div>
                                        </div>
                                        <div style={{ marginBottom: '12px' }}>
                                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold' }}>Юридический адрес</label>
                                            <input type="text" value={payerForm.legal_address} onChange={e => setPayerForm({ ...payerForm, legal_address: e.target.value })}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }} />
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                            <button type="button" onClick={handleSavePayer} style={{
                                                padding: '8px 18px',
                                                background: '#3498db',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer'
                                            }}>Сохранить реквизиты</button>
                                            <span style={{ fontSize: '12px', color: '#a3acb9' }}>ID: {access.payer.company_id}</span>
                                            {!access.payer.is_owner && <span style={{ fontSize: '12px', color: '#c0392b' }}>
                                                Пользователь не владелец этих реквизитов
                                            </span>}
                                        </div>
                                        {access.payer.same_inn_users > 0 && <div style={{
                                            marginTop: '12px',
                                            fontSize: '12px',
                                            color: '#697386'
                                        }}>
                                            Тот же ИНН встречается ещё у {access.payer.same_inn_users} польз. — это отдельные реквизиты, доступы и балансы между ними не связаны.
                                        </div>}
                                    </div>}

                                    <h3 style={{ fontSize: '16px' }}>Кабинеты ({access.cabinets.length})</h3>
                                    {access.cabinets.length === 0 && <div style={{ color: '#999', marginBottom: '25px' }}>Доступа к кабинетам нет.</div>}
                                    {access.cabinets.length > 0 && <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '25px' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '2px solid #eee', fontSize: '12px', color: '#697386' }}>
                                                <th style={{ padding: '8px', textAlign: 'left' }}>Кабинет</th>
                                                <th style={{ padding: '8px', textAlign: 'center' }}>Тариф</th>
                                                <th style={{ padding: '8px', textAlign: 'center' }}>Роль</th>
                                                <th style={{ padding: '8px', textAlign: 'left' }}>Откуда доступ</th>
                                                <th style={{ padding: '8px', textAlign: 'right' }}>Действия</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {access.cabinets.map(c => <tr key={c.membership_id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                                <td style={{ padding: '8px' }}>
                                                    <div style={{ fontWeight: '600' }}>
                                                        {c.account_name}
                                                        <span style={{ fontSize: '12px', color: '#999', fontWeight: 'normal' }}> · {c.marketplace_type}</span>
                                                        {!c.is_active && <span style={{ fontSize: '12px', color: '#c0392b' }}> · отключён</span>}
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#a3acb9' }}>{c.cabinet_id}</div>
                                                </td>
                                                <td style={{ padding: '8px', textAlign: 'center' }}>{c.plan_type}</td>
                                                <td style={{ padding: '8px', textAlign: 'center' }}>{c.role}</td>
                                                <td style={{ padding: '8px', fontSize: '12px' }}>
                                                    {c.is_owner && <span style={{ color: '#27ae60' }}>владелец</span>}
                                                    {!c.is_owner && c.source === 'direct' && <span>напрямую</span>}
                                                    {!c.is_owner && c.source === 'project' && <span style={{ color: '#697386' }}>
                                                        проект «{c.source_project_name || '—'}»
                                                    </span>}
                                                </td>
                                                <td style={{ padding: '8px', textAlign: 'right' }}>
                                                    {!c.is_owner && c.source === 'direct' && <>
                                                        <select value={c.role} onChange={e => handleCabinetRole(c.membership_id, e.target.value, c.role)} style={roleSelectStyle}>
                                                            {CABINET_ROLES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                                                        </select>
                                                        <button type="button" onClick={() => handleCabinetRevoke(c.membership_id, c.account_name)} style={{
                                                            background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer'
                                                        }}>Забрать</button>
                                                    </>}
                                                    {!c.is_owner && c.source === 'project' && <span style={{ fontSize: '12px', color: '#a3acb9' }}>
                                                        правится через проект
                                                    </span>}
                                                </td>
                                            </tr>)}
                                        </tbody>
                                    </table>}

                                    <h3 style={{ fontSize: '16px' }}>Проекты ({access.projects.length})</h3>
                                    {access.projects.length === 0 && <div style={{ color: '#999' }}>Проектов нет.</div>}
                                    {access.projects.length > 0 && <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '2px solid #eee', fontSize: '12px', color: '#697386' }}>
                                                <th style={{ padding: '8px', textAlign: 'left' }}>Проект</th>
                                                <th style={{ padding: '8px', textAlign: 'center' }}>Роль</th>
                                                <th style={{ padding: '8px', textAlign: 'center' }}>Кабинетов</th>
                                                <th style={{ padding: '8px', textAlign: 'center' }}>Участников</th>
                                                <th style={{ padding: '8px', textAlign: 'right' }}>Действия</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {access.projects.map(pr => <tr key={pr.workspace_id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                                <td style={{ padding: '8px' }}>
                                                    <div style={{ fontWeight: '600' }}>{pr.name}</div>
                                                    <div style={{ fontSize: '12px', color: '#a3acb9' }}>{pr.workspace_id}</div>
                                                </td>
                                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                                    {pr.is_owner ? <span style={{ color: '#27ae60' }}>владелец</span> : (pr.role || '—')}
                                                </td>
                                                <td style={{ padding: '8px', textAlign: 'center' }}>{pr.cabinets_count}</td>
                                                <td style={{ padding: '8px', textAlign: 'center' }}>{pr.members_count}</td>
                                                <td style={{ padding: '8px', textAlign: 'right' }}>
                                                    {pr.member_id && !pr.is_owner && <>
                                                        <select value={pr.role} onChange={e => handleProjectRole(pr.member_id, e.target.value, pr.role)} style={roleSelectStyle}>
                                                            {PROJECT_ROLES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                                                        </select>
                                                        <button type="button" onClick={() => handleProjectRemove(pr.member_id, pr.name)} style={{
                                                            background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer'
                                                        }}>Убрать</button>
                                                    </>}
                                                    {pr.is_owner && <span style={{ fontSize: '12px', color: '#a3acb9' }}>
                                                        владение меняется передачей проекта
                                                    </span>}
                                                </td>
                                            </tr>)}
                                        </tbody>
                                    </table>}
                                </>}
                            </div>}
                            {editTab === 'permissions' && <div>
                                    <h3 style={{
              marginTop: 0,
              fontSize: '16px'
            }}>Гранулярные права администратора</h3>
                                    <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '10px',
              marginBottom: '20px'
            }}>
                                        {Object.entries(availablePermissions).map(([key, label]) => <label key={key} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px',
                border: '1px solid #eee',
                borderRadius: '6px',
                cursor: 'pointer',
                background: userPermissions.includes(key) ? '#f0f7ff' : 'white'
              }}>
                                                <input type="checkbox" checked={userPermissions.includes(key)} onChange={e => {
                  if (e.target.checked) setUserPermissions([...userPermissions, key]);else setUserPermissions(userPermissions.filter(p => p !== key));
                }} />
                  
                                                <span style={{
                  fontSize: '13px'
                }}>{label}</span>
                                            </label>)}
                                    </div>
                                    <button onClick={handleSavePermissions} style={{
              width: '100%',
              padding: '12px',
              background: '#27ae60',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}>
                
                                        Обновить права
                                    </button>
                                </div>}
                        </div>
                    </div>
                </div>}

            {/* Bulk Actions Bar */}
            {selectedIds.length > 0 && <div style={{
      background: '#34495e',
      color: 'white',
      padding: '15px 25px',
      borderRadius: '12px',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    }}>
                    <div style={{
        fontWeight: 'bold'
      }}>Выбрано: {selectedIds.length}</div>
                    <div style={{
        display: 'flex',
        gap: '10px'
      }}>
                        <button onClick={() => handleBulkAction('ban')} style={{
          padding: '8px 15px',
          background: '#f39c12',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer'
        }}>Забанить</button>
                        <button onClick={() => handleBulkAction('unban')} style={{
          padding: '8px 15px',
          background: '#27ae60',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer'
        }}>Разбанить</button>
                        <button onClick={() => handleBulkAction('delete')} style={{
          padding: '8px 15px',
          background: '#e74c3c',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer'
        }}>Удалить</button>
                    </div>
                </div>}

            <div style={{
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
      overflow: 'hidden'
    }}>
                <table style={{
        width: '100%',
        borderCollapse: 'collapse'
      }}>
                    <thead style={{
          background: '#fcfcfc',
          borderBottom: '2px solid #eee'
        }}>
                        <tr>
                            <th style={{
              padding: '15px',
              textAlign: 'left',
              width: '40px'
            }}>
                                <input type="checkbox" onChange={handleSelectAll} checked={selectedIds.length === filteredUsers.length && filteredUsers.length > 0} />
                
                            </th>
                            <th style={{
              padding: '15px',
              textAlign: 'left'
            }}>Пользователь</th>
                            <th style={{
              padding: '15px',
              textAlign: 'left'
            }}>Контакты</th>
                            <th style={{
              padding: '15px',
              textAlign: 'center'
            }}>Роль</th>
                            <th style={{
              padding: '15px',
              textAlign: 'center'
            }}>Статус</th>
                            <th style={{
              padding: '15px',
              textAlign: 'right'
            }}>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan="6" style={{
              padding: '40px',
              textAlign: 'center'
            }}>Загрузка...</td></tr>}
                        {!loading && filteredUsers.length === 0 && <tr><td colSpan="6" style={{
              padding: '40px',
              textAlign: 'center',
              color: '#999'
            }}>Пользователи не найдены</td></tr>}
                        {filteredUsers.map(user => <tr key={user.id} style={{
            borderBottom: '1px solid #f5f5f5',
            opacity: (user.is_active === false || user.status === 'banned') ? 0.6 : 1,
            background: selectedIds.includes(user.id) ? '#f0f7ff' : 'transparent'
          }}>
                                <td style={{
              padding: '15px'
            }}>
                                    <input type="checkbox" checked={selectedIds.includes(user.id)} onChange={() => handleSelectOne(user.id)} />
                
                                </td>
                                <td style={{
              padding: '15px'
            }}>
                                    <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                                        <div style={{
                  fontWeight: '600'
                }}>{user.email}</div>
                                        {user.verified_identity && <span title="Подтвержден" style={{
                  color: '#3498db'
                }}>✅</span>}
                                        {user.email_verified && <span title="Email подтвержден" style={{
                  color: '#27ae60'
                }}>✉</span>}
                                        {user.anylex_recommended && <span title="Рекомендован" style={{
                  color: '#f1c40f'
                }}>⭐️</span>}
                                    </div>
                                    <div style={{
                fontSize: '12px',
                color: '#7f8c8d'
              }}>
                                        {user.first_name} {user.last_name} • <span style={{
                  fontSize: '12px'
                }}>ID: {user.id.slice(0, 8)}</span>
                                    </div>
                                </td>
                                <td style={{
              padding: '15px',
              fontSize: '13px'
            }}>
                                    <div>{user.phone || '—'}</div>
                                    {user.telegram_id && <div style={{
                fontSize: '12px',
                color: '#2980b9'
              }}>TG: {user.telegram_id}</div>}
                                </td>
                                <td style={{
              padding: '15px',
              textAlign: 'center'
            }}>
                                    {getRoleBadge(user.role)}
                                </td>
                                <td style={{
              padding: '15px',
              textAlign: 'center'
            }}>
                                    <div style={{
                fontSize: '12px',
                color: '#666'
              }}>
                                        {(user.is_active === false || user.status === 'banned') ? 'Заблокирован' : 'Активен'}<br />
                                        {fmtDate(user.created_at)}
                                    </div>
                                </td>
                                <td style={{
              padding: '15px',
              textAlign: 'right',
              whiteSpace: 'nowrap'
            }}>
                                    <button onClick={() => handleEdit(user)} style={{
                marginRight: '10px',
                padding: '6px 10px',
                border: 'none',
                color: '#3498db',
                background: 'transparent',
                cursor: 'pointer'
              }}>Редакт.</button>
                                    <button onClick={() => handleStatusToggle(user)} style={{
                marginRight: '10px',
                padding: '6px 10px',
                border: 'none',
                color: (user.is_active === false || user.status === 'banned') ? '#27ae60' : '#f39c12',
                background: 'transparent',
                cursor: 'pointer'
              }}>
                                        {(user.is_active === false || user.status === 'banned') ? 'Разбан' : 'Бан'}
                                    </button>
                                    <button onClick={() => handleDelete(user.id)} style={{
                padding: '6px 10px',
                border: 'none',
                color: '#e74c3c',
                background: 'transparent',
                cursor: 'pointer'
              }}>Удалить</button>
                                </td>
                            </tr>)}
                    </tbody>
                </table>
            </div>
            <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '15px',
        marginTop: '15px'
      }}>
                <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0 || loading} style={{
          padding: '8px 16px'
        }}>← Назад</button>
                <span style={{
          color: '#666',
          fontSize: '14px'
        }}>Страница {page + 1}</span>
                <button onClick={() => setPage(p => p + 1)} disabled={!hasMore || loading} style={{
          padding: '8px 16px'
        }}>Вперёд →</button>
            </div>
        </div>;
}
export default AdminUsers;
